import { useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  getArticlesBySection,
  searchArticles,
  type HelpArticle,
} from "@/lib/help/manifest";

type HelpLayoutProps = {
  activeSlug?: string;
  children: React.ReactNode;
};

export function HelpLayout({ activeSlug, children }: HelpLayoutProps) {
  const sections = useMemo(() => getArticlesBySection(), []);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const results = useMemo(() => (query.trim() ? searchArticles(query) : []), [query]);

  return (
    <div className="mx-auto flex w-full max-w-7xl gap-6">
      <aside className="hidden w-72 shrink-0 md:block">
        <div className="sticky top-4">
          <div className="mb-3 flex items-center gap-2 px-2">
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Help Center</h2>
          </div>
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search help"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <ScrollArea className="h-[calc(100vh-12rem)] pr-2">
            {query.trim() ? (
              <SearchResults results={results} onPick={(slug) => navigate(`/help/${slug}`)} />
            ) : (
              <nav className="space-y-4">
                {sections.map((s) => (
                  <div key={s.section}>
                    <p className="px-2 pb-1 text-xs font-bold uppercase tracking-wide text-[hsl(var(--primary-glow))]">
                      {s.label}
                    </p>
                    <ul className="space-y-0.5">
                      {s.articles.map((a) => (
                        <li key={a.slug}>
                          <NavLink
                            to={`/help/${a.slug}`}
                            className={({ isActive }) =>
                              cn(
                                "block rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-primary hover:text-primary-foreground",
                                (isActive || a.slug === activeSlug) &&
                                  "bg-primary font-medium text-primary-foreground",
                              )
                            }

                          >

                            {a.title}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>
            )}
          </ScrollArea>
        </div>
      </aside>

      <main className="min-w-0 flex-1">{children}</main>
    </div>
  );
}

function SearchResults({
  results,
  onPick,
}: {
  results: HelpArticle[];
  onPick: (slug: string) => void;
}) {
  if (results.length === 0) {
    return <p className="px-2 text-sm text-muted-foreground">No matches.</p>;
  }
  return (
    <ul className="space-y-1">
      {results.map((a) => (
        <li key={a.slug}>
          <button
            type="button"
            onClick={() => onPick(a.slug)}
            className="block w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            <div className="font-medium">{a.title}</div>
            <div className="text-xs text-muted-foreground">{a.sectionLabel}</div>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function HelpArticleBody({ article }: { article: HelpArticle }) {
  return (
    <article className="prose prose-neutral max-w-3xl dark:prose-invert prose-headings:scroll-mt-20">
      <header className="not-prose mb-6">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {article.sectionLabel}
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">{article.title}</h1>
        {article.summary && (
          <p className="mt-2 text-base text-muted-foreground">{article.summary}</p>
        )}
      </header>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body}</ReactMarkdown>
      <footer className="not-prose mt-12 border-t border-border pt-6 text-sm text-muted-foreground">
        Didn't answer your question?{" "}
        <Link to="/help" className="text-foreground underline-offset-2 hover:underline">
          Browse the help center
        </Link>{" "}
        or use the <strong>?</strong> button in the bottom-right inside Excerptus to send us a message.
      </footer>
    </article>
  );
}
